import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, ListGroup, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import Layout from '../../components/ui/Layout';
import NoIndexHelmet from '../../components/seo/NoIndexHelmet';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { useAuth } from '../../components/context/AuthContext';
import { useNotification } from '../../components/ui/Notification';
import ShoppingListFormModal from '../../components/shoppingList/ShoppingListFormModal';
import {
  addShoppingListToCart,
  deleteShoppingList,
  getSupermarketInventoryMap,
  subscribeToUserShoppingLists,
} from '../../services/shoppingListService';
import { getItemAvailability, summarizeListAvailability } from '../../utils/shoppingListUtils';
import { InventoryAvailabilityMap, ShoppingList } from '../../types/shoppingList';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ShoppingListsPage');

function formatNotificationDate(timestamp: Timestamp | null): string | null {
  if (!timestamp) return null;
  return timestamp
    .toDate()
    .toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function isReminderDue(timestamp: Timestamp | null): boolean {
  if (!timestamp) return false;
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  return timestamp.toDate().getTime() <= endOfToday.getTime();
}

export default function ShoppingListsPage() {
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [inventoryMaps, setInventoryMaps] = useState<Record<string, InventoryAvailabilityMap>>({});
  const [editingList, setEditingList] = useState<ShoppingList | null>(null);
  const [creatingCartForListId, setCreatingCartForListId] = useState<string | null>(null);
  const [deletingListId, setDeletingListId] = useState<string | null>(null);

  const breadcrumbItems = [
    { path: '/', label: 'Home' },
    { label: 'Shopping Lists' },
  ];

  useEffect(() => {
    if (!currentUser) {
      setLists([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = subscribeToUserShoppingLists(
      currentUser.uid,
      (fetchedLists) => {
        setLists(fetchedLists);
        setLoadError('');
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching shopping lists', error);
        setLoadError('Failed to load your shopping lists. Please try again.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [currentUser]);

  // Fetch inventory once per supermarket so availability can be shown per list.
  useEffect(() => {
    const supermarketIds = Array.from(new Set(lists.map((list) => list.supermarketId)));
    const missingSupermarketIds = supermarketIds.filter((id) => !inventoryMaps[id]);
    if (missingSupermarketIds.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const supermarketId of missingSupermarketIds) {
        try {
          const inventoryMap = await getSupermarketInventoryMap(supermarketId);
          if (!cancelled) {
            setInventoryMaps((previous) => ({ ...previous, [supermarketId]: inventoryMap }));
          }
        } catch (error) {
          logger.error(`Failed to fetch inventory for supermarket ${supermarketId}`, error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lists, inventoryMaps]);

  const handleDeleteList = async (list: ShoppingList) => {
    if (!window.confirm(`Delete the shopping list "${list.name}"? This cannot be undone.`)) {
      return;
    }
    setDeletingListId(list.id);
    try {
      await deleteShoppingList(list.id);
      showNotification({
        type: 'success',
        title: 'Shopping List Deleted',
        message: `"${list.name}" was deleted.`,
        duration: 3000,
      });
    } catch (error) {
      logger.error('Failed to delete shopping list', error);
      showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete the shopping list. Please try again.',
        duration: 5000,
      });
    } finally {
      setDeletingListId(null);
    }
  };

  const handleCreateCart = async (list: ShoppingList) => {
    if (!currentUser) return;
    setCreatingCartForListId(list.id);
    try {
      let inventoryMap = inventoryMaps[list.supermarketId];
      if (!inventoryMap) {
        inventoryMap = await getSupermarketInventoryMap(list.supermarketId);
        setInventoryMaps((previous) => ({ ...previous, [list.supermarketId]: inventoryMap }));
      }

      const result = await addShoppingListToCart(currentUser.uid, list, inventoryMap);

      if (result.addedProducts === 0) {
        showNotification({
          type: 'warning',
          title: 'Nothing Available',
          message: `None of the products on "${list.name}" are currently in stock at ${list.supermarketName}.`,
          duration: 6000,
        });
        return;
      }

      let message = `Added ${result.addedProducts} of ${result.totalProducts} products from "${list.name}" to your cart at ${list.supermarketName}.`;
      if (result.skippedProductNames.length > 0) {
        message += ` Out of stock: ${result.skippedProductNames.join(', ')}.`;
      }
      if (result.reducedQuantityNames.length > 0) {
        message += ` Quantity reduced to available stock: ${result.reducedQuantityNames.join(', ')}.`;
      }

      showNotification({
        type: 'success',
        title: 'Cart Created',
        message,
        duration: 6000,
      });
      navigate('/carts');
    } catch (error) {
      logger.error('Failed to create cart from shopping list', error);
      showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to create a cart from this list. Please try again.',
        duration: 5000,
      });
    } finally {
      setCreatingCartForListId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <Spinner animation="border" className="me-2" />
          <p>Loading your shopping lists...</p>
        </Container>
      </Layout>
    );
  }

  if (!currentUser) {
    return (
      <Layout>
        <Container className="mt-4 px-3">
          <Row className="align-items-center mb-1">
            <Col>
              <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
          </Row>
          <div className="text-center mt-5 mb-5">
            <h2 className="mb-4">Shopping Lists</h2>
            <div className="mb-4">
              <i className="fas fa-list fa-4x text-muted mb-3"></i>
              <p className="text-muted fs-5">
                Please log in to view and manage your shopping lists.
              </p>
            </div>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-5">
              <Button
                variant="primary"
                size="lg"
                onClick={() =>
                  navigate(`/login?path=${encodeURIComponent(window.location.pathname)}`)
                }
                className="px-4"
              >
                <i className="fas fa-sign-in-alt me-2"></i>
                Log In
              </Button>
              <Button variant="outline-secondary" size="lg" onClick={() => navigate('/')} className="px-4">
                <i className="fas fa-home me-2"></i>
                Browse Products
              </Button>
            </div>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <NoIndexHelmet title="Shopping Lists | Pack My Cart" path="/shopping-lists" />
      <Container className="mt-4 px-3" data-testid="shopping-lists-container">
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
              &larr; Go Back
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mt-3 gap-2">
          <h2 className="mb-0">Shopping Lists</h2>
          <Button variant="outline-primary" onClick={() => navigate('/carts')}>
            <i className="fas fa-shopping-cart me-2"></i>
            Go to Cart
          </Button>
        </div>

        {loadError && <Alert variant="danger">{loadError}</Alert>}

        {lists.length === 0 ? (
          <Card className="text-center mb-5">
            <Card.Body className="py-5">
              <i className="fas fa-list fa-3x text-muted mb-3"></i>
              <p className="text-muted fs-5 mb-3">You have no shopping lists yet.</p>
              <p className="text-muted">
                Add products to your cart, then use <strong>Save as List</strong> on the cart page to
                create a reusable shopping list.
              </p>
              <Button variant="primary" onClick={() => navigate('/carts')}>
                Open Your Cart
              </Button>
            </Card.Body>
          </Card>
        ) : (
          lists.map((list) => {
            const inventoryMap = inventoryMaps[list.supermarketId] || {};
            const summary = summarizeListAvailability(list.items, inventoryMap);
            const notificationDateLabel = formatNotificationDate(list.notificationDate);
            const reminderDue = isReminderDue(list.notificationDate);

            return (
              <Card key={list.id} className="mb-4 shadow-sm border-primary">
                <Card.Header className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="fw-bold">{list.name}</span>
                    <Badge bg="secondary">
                      <i className="fas fa-store me-1"></i>
                      {list.supermarketName}
                    </Badge>
                    {notificationDateLabel && (
                      <Badge bg={reminderDue ? 'warning' : 'info'} text={reminderDue ? 'dark' : undefined}>
                        <i className="fas fa-bell me-1"></i>
                        {reminderDue ? 'Reminder due: ' : 'Notify on: '}
                        {notificationDateLabel}
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Badge
                      bg={summary.missingItems > 0 ? 'warning' : 'success'}
                      text={summary.missingItems > 0 ? 'dark' : undefined}
                    >
                      {summary.availableItems} of {summary.totalItems} products available
                    </Badge>
                    {summary.missingItems > 0 && (
                      <Badge bg="danger">
                        {summary.missingItems} missing
                      </Badge>
                    )}
                  </div>
                </Card.Header>
                <ListGroup variant="flush">
                  {list.items.map((item) => {
                    const availability = getItemAvailability(item.productId, inventoryMap);
                    const outOfStock = !availability.inStock;
                    const lowStock =
                      availability.inStock && availability.availableQuantity < item.quantity;
                    return (
                      <ListGroup.Item
                        key={item.productId}
                        className={`d-flex align-items-center justify-content-between py-2 px-3 ${
                          outOfStock ? 'text-decoration-line-through text-muted' : ''
                        }`}
                      >
                        <div className="flex-grow-1 me-2">
                          <span className="fw-semibold">{item.name}</span>
                          <small className="d-block">
                            ${item.price.toFixed(2)} each &middot; Qty: {item.quantity}
                          </small>
                        </div>
                        {outOfStock && (
                          <Badge bg="danger" className="text-decoration-none">
                            Out of stock
                          </Badge>
                        )}
                        {lowStock && (
                          <Badge bg="warning" text="dark">
                            Only {availability.availableQuantity} in stock
                          </Badge>
                        )}
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
                <Card.Footer className="d-flex flex-wrap gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleCreateCart(list)}
                    disabled={summary.availableItems === 0 || creatingCartForListId === list.id}
                  >
                    {creatingCartForListId === list.id ? (
                      <>
                        <Spinner as="span" size="sm" className="me-1" />
                        Creating Cart...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-cart-plus me-1"></i>
                        Create Cart ({summary.availableItems} of {summary.totalItems})
                      </>
                    )}
                  </Button>
                  <Button variant="outline-primary" size="sm" onClick={() => setEditingList(list)}>
                    <i className="fas fa-edit me-1"></i>
                    Edit List
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteList(list)}
                    disabled={deletingListId === list.id}
                  >
                    {deletingListId === list.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </Card.Footer>
              </Card>
            );
          })
        )}

        {currentUser && (
          <ShoppingListFormModal
            show={editingList !== null}
            mode="edit"
            userId={currentUser.uid}
            listId={editingList?.id}
            initialName={editingList?.name || ''}
            initialSupermarketId={editingList?.supermarketId || ''}
            initialSupermarketName={editingList?.supermarketName || ''}
            initialNotificationDate={editingList?.notificationDate || null}
            initialItems={editingList?.items || []}
            onHide={() => setEditingList(null)}
            onSaved={(message) => {
              setEditingList(null);
              showNotification({
                type: 'success',
                title: 'Shopping List Updated',
                message,
                duration: 4000,
              });
            }}
          />
        )}

        <div className="mb-5"></div>
      </Container>
    </Layout>
  );
}
